import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from '../../Utils/reactIntlCompat';
import { Controller, useFormContext } from 'react-hook-form';
import { TextField, InputAdornment } from '@mui/material';
import { useFieldRequired } from './fieldRequiredContext';

const normalizeNumberValue = (rawValue) => {
  const value = `${rawValue ?? ''}`.trim();

  if (!value) return '';

  // Accept the comma as the decimal separator from the UI and normalize it to a dot.
  if (value.includes(',')) {
    return value.replace(/\./g, '').replace(',', '.');
  }

  // Treat a dot-separated group of three digits as thousands formatting, not a decimal.
  if (value.includes('.')) {
    const parts = value.split('.');

    if (parts.length > 2) {
      return parts.join('');
    }

    const [integerPart, fractionalPart] = parts;

    if (fractionalPart?.length === 3) {
      return `${integerPart}${fractionalPart}`;
    }
  }

  return value;
};

const formatNumberValue = (rawValue) => {
  const rawString = `${rawValue ?? ''}`.trim();
  const value = normalizeNumberValue(rawString);

  if (!value) return '';

  // Keep a trailing separator visible while the user is still typing a decimal value.
  const hasTrailingSeparator = /[.,]$/.test(rawString);

  const [integerPart, fractionalPart = ''] = value.split('.');
  // Rebuild the integer part with thousands separators for display.
  const formattedIntegerPart = integerPart.replace(
    /\B(?=(\d{3})+(?!\d))/g,
    '.'
  );

  if (hasTrailingSeparator && !fractionalPart) {
    return `${formattedIntegerPart},`;
  }

  return fractionalPart
    ? `${formattedIntegerPart},${fractionalPart}`
    : formattedIntegerPart;
};

const FormTextFieldInput = function FormTextFieldInput({
  allUpperCase,
  clearErrors,
  disabled,
  error,
  icon,
  label,
  maxCharacters,
  multiline,
  name,
  nameTranslation,
  helperText,
  onChange,
  required,
  rows,
  setValue,
  type,
  value,
  width,
  intl,
}) {
  const [displayValue, setDisplayValue] = React.useState(() =>
    type === 'number' ? formatNumberValue(value) : value || ''
  );

  React.useEffect(() => {
    if (type !== 'number') {
      setDisplayValue(value || '');
      return;
    }

    const nextDisplayValue = formatNumberValue(value);

    // Keep the user's in-progress typing unless the normalized value really changed.
    setDisplayValue((currentDisplayValue) => {
      if (
        normalizeNumberValue(currentDisplayValue) ===
        normalizeNumberValue(nextDisplayValue)
      ) {
        return currentDisplayValue;
      }

      return nextDisplayValue;
    });
  }, [type, value]);

  return (
    <TextField
      id={`${name}-textField`}
      value={type === 'number' ? displayValue : value || ''}
      onChange={(e) => {
        const nextValue = e.target.value;

        clearErrors(name);

        if (maxCharacters && nextValue.length > maxCharacters) {
          return;
        }

        if (type === 'number') {
          // Store the normalized number in the form state, but keep the raw text visible.
          setDisplayValue(nextValue);
          onChange(normalizeNumberValue(nextValue));
          return;
        }

        onChange(nextValue);

        if (allUpperCase) setValue(name, nextValue.toUpperCase());
      }}
      FormHelperTextProps={{
        sx: {
          whiteSpace: 'noWrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
      }}
      helperText={helperText || ' '}
      error={error}
      sx={{ width: width !== '100%' ? width : null }}
      size="medium"
      type={type === 'number' ? 'text' : type}
      inputProps={
        type === 'number'
          ? {
              inputMode: 'decimal',
              pattern: '[0-9.,]*',
            }
          : undefined
      }
      label={label || nameTranslation}
      fullWidth={width === '100%'}
      required={required}
      placeholder={`${intl.messages.INSERT} ${label || nameTranslation}`}
      variant="outlined"
      disabled={disabled}
      multiline={multiline}
      rows={multiline ? rows : null}
      InputProps={{
        endAdornment: icon ? (
          <InputAdornment position="end">{icon}</InputAdornment>
        ) : null,
      }}
    />
  );
};

const FormTextField = function FormTextField({
  intl,
  name,
  label,
  defaultValue = '',
  required = false,
  width = '100%',
  disabled = false,
  type = 'text',
  icon = null,
  multiline = false,
  rows = null,
  allUpperCase = false,
  maxCharacters = null,
  error = null,
  validateCallback = null,
}) {
  const { control, setValue, clearErrors } = useFormContext();
  const { message = null } = error || {};
  const effectiveRequired = useFieldRequired(required);

  const nameTranslation = Object.prototype.hasOwnProperty.call(
    intl.messages,
    name?.toUpperCase()
  )
    ? intl.messages[name.toUpperCase()]
    : null;

  return (
    <>
      <style>
        {
          //removes the arrows in button type field
          `input::-webkit-outer-spin-button,
            input::-webkit-inner-spin-button {
            -webkit-appearance: none;
              margin: 0;
            }`
        }
      </style>
      <Controller
        control={control}
        name={name}
        defaultValue={defaultValue}
        rules={{
          required: {
            value: effectiveRequired,
            message: intl.messages.MANDATORY_FIELD,
          },
          validate: validateCallback || {},
        }}
        render={({ field: { value, onChange } }) => (
          <FormTextFieldInput
            allUpperCase={allUpperCase}
            clearErrors={clearErrors}
            disabled={disabled}
            error={error}
            icon={icon}
            label={label}
            maxCharacters={maxCharacters}
            multiline={multiline}
            name={name}
            nameTranslation={nameTranslation}
            helperText={message}
            onChange={onChange}
            required={effectiveRequired}
            rows={rows}
            setValue={setValue}
            type={type}
            value={value}
            width={width}
            intl={intl}
          />
        )}
      />
    </>
  );
};

FormTextField.propTypes = {
  name: PropTypes.string.isRequired,
  defaultValue: PropTypes.string,
  required: PropTypes.bool,
  width: PropTypes.string,
  intl: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  type: PropTypes.string,
  icon: PropTypes.node,
  multiline: PropTypes.bool,
  allUpperCase: PropTypes.bool,
  maxCharacters: PropTypes.number,
  validateCallback: PropTypes.func,
  error: PropTypes.object,
  label: PropTypes.string,
};

export default injectIntl(FormTextField);
